import { Select } from '@tempo-io/sui-select';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import type { CSSObjectWithLabel } from 'react-select';
import { ImputedBadge } from '@/features/items/components/data-table/cells/ImputedBadge';
import { SettingsGroupLabel } from './SettingsLink';
import type { EstimationGroup, EstimationOption } from './useGroupedEstimationOptions';
import {
  ESTIMATION_MENU_WIDTH,
  filterEstimationOption,
  formatGroupLabel,
  isSettingsOption,
} from './useGroupedEstimationOptions';

const SELECT_DEFAULT_HEIGHT = 36;

interface GroupedEstimationSelectProps {
  value: EstimationOption | null;
  onChange: (option: EstimationOption | null) => void;
  groupedOptions: EstimationGroup[];
  isLoading?: boolean;
  isImputed?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  id?: string;
  name?: string;
  className?: string;
  placeholder?: ReactNode;
  inlineEdit?: boolean;
  cellEdit?: boolean;
  height?: number;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  menuPortalTarget?: HTMLElement | null;
  menuPosition?: 'absolute' | 'fixed';
  menuZIndex?: number;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  autoFocus?: boolean;
  openMenuOnFocus?: boolean;
  settingsOptionLabel?: ReactNode;
}

export const GroupedEstimationSelect = ({
  value,
  onChange,
  groupedOptions,
  isLoading,
  isImputed,
  isDisabled,
  isClearable = true,
  id,
  name,
  className,
  placeholder,
  inlineEdit,
  cellEdit,
  height = SELECT_DEFAULT_HEIGHT,
  menuPlacement,
  menuPortalTarget,
  menuPosition,
  menuZIndex,
  onMenuOpen,
  onMenuClose,
  autoFocus,
  openMenuOnFocus,
  settingsOptionLabel,
}: GroupedEstimationSelectProps) => {
  const selectStyles = useMemo(
    () => ({
      menu: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...base,
        width: '100%',
        minWidth: ESTIMATION_MENU_WIDTH,
      }),
      menuPortal: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...base,
        ...(menuZIndex !== undefined && { zIndex: menuZIndex }),
        width: 'max-content',
        minWidth: ESTIMATION_MENU_WIDTH,
      }),
    }),
    [menuZIndex],
  );

  const formatOptionLabel = useCallback(
    (option: EstimationOption, context: { context: 'menu' | 'value' }) => {
      if (isSettingsOption(option)) {
        return settingsOptionLabel ?? <SettingsGroupLabel />;
      }
      if (context.context === 'value') {
        if (isImputed) {
          return (
            <div className="flex items-center gap-2 w-full">
              <ImputedBadge />
              <span>{option.label}</span>
            </div>
          );
        }
        return option.label;
      }
      return (
        <div className="flex items-center justify-between gap-2 w-full">
          <span>{option.label}</span>
          {option.effortRange && (
            <span className="text-text-neutral-secondary text-xs">{option.effortRange}</span>
          )}
        </div>
      );
    },
    [isImputed, settingsOptionLabel],
  );

  return (
    <Select<EstimationOption, false, EstimationGroup>
      id={id}
      name={name}
      className={className}
      isClearable={isClearable}
      inlineEdit={inlineEdit}
      cellEdit={cellEdit}
      height={height}
      menuPortalTarget={menuPortalTarget}
      menuPlacement={menuPlacement}
      menuPosition={menuPosition}
      styles={selectStyles}
      autoFocus={autoFocus}
      openMenuOnFocus={openMenuOnFocus}
      value={value}
      onChange={onChange}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      options={groupedOptions}
      isLoading={isLoading}
      isDisabled={isDisabled}
      placeholder={placeholder}
      menuWidth={ESTIMATION_MENU_WIDTH}
      formatGroupLabel={formatGroupLabel}
      filterOption={filterEstimationOption}
      isOptionDisabled={isSettingsOption}
      formatOptionLabel={formatOptionLabel}
      closeMenuOnScroll={(e: Event) =>
        e.target instanceof HTMLElement && !e.target.closest('[class*="-menu"]')
      }
    />
  );
};
